// Výkaz práce — filtr řádků podle `payment_status` (server + client).

import type { ObjectLiteral, SelectQueryBuilder } from 'typeorm';
import {
  WORK_REPORT_PAYMENT_FILTER_OPTIONS,
  WORK_REPORT_PAYMENT_STATUS,
  type WorkReportPaymentStatus,
  normalizeWorkReportPaymentStatus,
} from '../constants/work-report.constants';

/** Rozparsuje CSV / opakovaný query param na platné stavy proplacení (bez duplicit). */
export function parseWorkReportPaymentStatusCsv(
  raw: string | string[] | null | undefined,
): WorkReportPaymentStatus[] {
  if (raw == null) return [];
  const items = Array.isArray(raw) ? raw : [raw];
  const out: WorkReportPaymentStatus[] = [];
  for (const item of items) {
    for (const part of String(item ?? '').split(',')) {
      const v = part.trim().toLowerCase();
      if (
        v === WORK_REPORT_PAYMENT_STATUS.UNPAID ||
        v === WORK_REPORT_PAYMENT_STATUS.INVOICED ||
        v === WORK_REPORT_PAYMENT_STATUS.PAID
      ) {
        if (!out.includes(v)) out.push(v);
      }
    }
  }
  return out;
}

/**
 * Převod výběru z checkbox filtru na query `paymentStatus`.
 * Prázdný výběr nebo všechny tři možnosti = bez filtru (všechny řádky).
 */
export function resolveWorkReportPaymentStatusServerFilter(
  selected: string[] | null | undefined,
): WorkReportPaymentStatus[] | undefined {
  const statuses = parseWorkReportPaymentStatusCsv(selected);
  if (statuses.length === 0) return undefined;
  if (statuses.length >= WORK_REPORT_PAYMENT_FILTER_OPTIONS.length) return undefined;
  return statuses;
}

/** Je výběr filtru proplacení ve výchozím stavu (pouze neproplacené)? */
export function isWorkReportPaymentFilterDefaultSelected(
  selected: string[] | null | undefined,
): boolean {
  const resolved = resolveWorkReportPaymentStatusServerFilter(selected);
  return resolved?.length === 1 && resolved[0] === WORK_REPORT_PAYMENT_STATUS.UNPAID;
}

/**
 * Badge „Neproplacené“ je zbytečný, když filtr už omezuje seznam jen na neproplacené
 * (tab / default „Neproplacené“). Zobrazuj status proplacení až ve „Vše“ / smíšeném filtru.
 */
export function shouldShowWorkReportPaymentStatusBadge(
  selected: string[] | null | undefined,
): boolean {
  return !isWorkReportPaymentFilterDefaultSelected(selected);
}

/** Client-side: řádek projde, pokud je filtr prázdný nebo stav je ve výběru. */
export function matchesWorkReportPaymentStatusFilter(
  paymentStatus: string | null | undefined,
  selected: string[] | null | undefined,
): boolean {
  const resolved = resolveWorkReportPaymentStatusServerFilter(selected);
  if (!resolved?.length) return true;
  const normalized = normalizeWorkReportPaymentStatus(paymentStatus);
  return resolved.includes(normalized);
}

/**
 * Server-side filtr `payment_status` pro TypeORM query builder.
 * `unpaid` zahrnuje i legacy NULL hodnoty.
 */
export function applyWorkReportPaymentStatusFilter<Entity extends ObjectLiteral>(
  qb: SelectQueryBuilder<Entity>,
  alias: string,
  statuses: WorkReportPaymentStatus[] | undefined,
  paramPrefix = 'wrPay',
): void {
  if (!statuses?.length) return;

  const parts: string[] = [];
  const params: Record<string, string> = {};

  if (statuses.includes(WORK_REPORT_PAYMENT_STATUS.UNPAID)) {
    parts.push(`(${alias}.payment_status = :${paramPrefix}Unpaid OR ${alias}.payment_status IS NULL)`);
    params[`${paramPrefix}Unpaid`] = WORK_REPORT_PAYMENT_STATUS.UNPAID;
  }
  if (statuses.includes(WORK_REPORT_PAYMENT_STATUS.INVOICED)) {
    parts.push(`${alias}.payment_status = :${paramPrefix}Invoiced`);
    params[`${paramPrefix}Invoiced`] = WORK_REPORT_PAYMENT_STATUS.INVOICED;
  }
  if (statuses.includes(WORK_REPORT_PAYMENT_STATUS.PAID)) {
    parts.push(`${alias}.payment_status = :${paramPrefix}Paid`);
    params[`${paramPrefix}Paid`] = WORK_REPORT_PAYMENT_STATUS.PAID;
  }

  if (parts.length > 0) {
    qb.andWhere(`(${parts.join(' OR ')})`, params);
  }
}
